/**
 * Prix affiché AVANT le passage sur Stripe (étape Infos).
 *
 * Le produit Stripe est multi-devise (un seul PRICE_ID : 49 € / 59 $ / 45 £) et
 * c'est Stripe qui choisit la devise à partir de la localisation du client au
 * checkout — la console ne peut donc qu'ESTIMER ce que le client verra. On se
 * base sur le fuseau horaire (US, Royaume-Uni), puis sur la région de la langue
 * du navigateur (en-US, en-GB…). En cas de
 * doute → euros (la majorité du parc). Le montant réel est de toute façon
 * affiché par Stripe sur la page de paiement.
 */

export type TrialPrice = {
    /** Montant formaté, prêt à afficher (ex. « 49 € », « $59 », « £45 »). */
    label: string;
    currency: 'eur' | 'usd' | 'gbp';
};

// Espace insécable : « 49 €/mois » ne doit pas se couper entre le nombre et le symbole.
const PRICES: Record<TrialPrice['currency'], string> = {
    eur: '49 €',
    usd: '$59',
    gbp: '£45',
};

const US_TIMEZONES = new Set([
    'America/New_York',
    'America/Detroit',
    'America/Chicago',
    'America/Denver',
    'America/Phoenix',
    'America/Los_Angeles',
    'America/Anchorage',
    'Pacific/Honolulu',
]);

const UK_TIMEZONES = new Set([
    'Europe/London',
    'Europe/Belfast',
    'Europe/Jersey',
    'Europe/Guernsey',
    'Europe/Isle_of_Man',
]);

function detectRegion(): string | undefined {
    if (typeof navigator === 'undefined') return undefined;
    const langs = navigator.languages?.length ? navigator.languages : [navigator.language];
    for (const tag of langs) {
        try {
            const { region } = new Intl.Locale(tag);
            if (region) return region.toUpperCase();
        } catch {
            /* tag invalide : on passe au suivant */
        }
    }
    return undefined;
}

function detectCurrency(): TrialPrice['currency'] {
    // Stripe choisit la devise selon la LOCALISATION du client : le fuseau horaire
    // passe donc avant la langue (un téléphone britannique réglé en « English (US) »
    // annonçait $59 ici puis £45 chez Stripe).
    // Fuseaux US uniquement — « America/* » couvre aussi Canada, Mexique, Brésil…
    // que Stripe facture en euros (pas de prix dans leur devise).
    try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
        if (US_TIMEZONES.has(tz)) return 'usd';
        if (UK_TIMEZONES.has(tz)) return 'gbp';
    } catch {
        /* Intl indisponible : la langue départage */
    }

    const region = detectRegion();
    if (region === 'US') return 'usd';
    if (region === 'GB') return 'gbp';
    return 'eur';
}

/** Formule annuelle « 12 mois pour le prix de 10 » (prix Stripe dédié, mêmes devises). */
const ANNUAL_PRICES: Record<TrialPrice['currency'], string> = {
    eur: '490\u00a0€',
    usd: '$590',
    gbp: '£450',
};

/** Prix annuel affiché pour la devise donnée (Stripe : `eur`/`usd`/`gbp`), sinon estimée ; euros par défaut. */
export function annualPriceLabel(currency?: string | null): string {
    const key = (currency || detectCurrency()).toLowerCase() as TrialPrice['currency'];
    return ANNUAL_PRICES[key] || ANNUAL_PRICES.eur;
}

/** Formule Pro « Ma marque » (69 € / 79 $ / 59 £ par mois, 690 € / 790 $ / 590 £ par an). */
const PRO_PRICES: Record<TrialPrice['currency'], { month: string; year: string }> = {
    eur: { month: '69\u00a0€', year: '690\u00a0€' },
    usd: { month: '$79', year: '$790' },
    gbp: { month: '£59', year: '£590' },
};

/** Prix Pro affiché pour la devise donnée, sinon estimée ; euros par défaut. */
export function proPriceLabel(period: 'month' | 'year', currency?: string | null): string {
    const key = (currency || detectCurrency()).toLowerCase() as TrialPrice['currency'];
    return (PRO_PRICES[key] || PRO_PRICES.eur)[period];
}

export function getTrialPrice(): TrialPrice {
    const currency = detectCurrency();
    return { label: PRICES[currency], currency };
}

/** Prix mensuel pour une devise Stripe connue (`eur`/`usd`/`gbp`), sinon euros. */
export function priceLabelFor(currency: string | null | undefined): string {
    const key = (currency || '').toLowerCase() as TrialPrice['currency'];
    return PRICES[key] || PRICES.eur;
}

/** Montant Stripe (centimes) formaté dans la devise, sans décimales inutiles (« 45 € », « $59 »). */
export function formatAmount(cents: number, currency: string, lang: string): string {
    try {
        return new Intl.NumberFormat(lang, {
            style: 'currency',
            currency: currency.toUpperCase(),
            minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
            maximumFractionDigits: 2,
        }).format(cents / 100);
    } catch {
        return priceLabelFor(currency);
    }
}

/**
 * Date formatée dans la langue de l'interface (ex. « 22 septembre »). Mois en
 * toutes lettres : la forme courte ajoute un point dans plusieurs langues
 * (« sept. ») qui double celui de la phrase.
 */
export function formatLongDate(date: Date, lang: string, now: Date = new Date()): string {
    try {
        // Année affichée seulement si ce n'est pas l'année en cours (« 6 janvier 2027 »).
        const withYear = date.getFullYear() !== now.getFullYear();
        return new Intl.DateTimeFormat(lang, {
            day: 'numeric',
            month: 'long',
            ...(withYear && { year: 'numeric' }),
        }).format(date);
    } catch {
        return date.toLocaleDateString();
    }
}

/** Aujourd'hui + n jours, formaté comme `formatLongDate`. */
export function formatDayOffset(days: number, lang: string): string {
    const date = new Date();
    date.setDate(date.getDate() + days);
    return formatLongDate(date, lang);
}
